import React, { useState } from 'react'
import i18n from '../../../../i18n'
import { User, Sparkles, Users, Check } from 'lucide-react'
import { Button, Dialog } from '../../../../components/ui'
import { BasicInfoTab, PreferencesTab, SellerTab } from '../../../../components/leads/tabs'
import { Lead, LeadInterest } from '../../../../types'
import { LeadEditFormData, LeadEditFormattedValues } from '../../domain/interfaces'
import { useVehicleOptions } from '../../../../hooks/useVehicleOptions'
import { useLeadOptions } from '../../../../hooks/useLeadOptions'
import { useUsers } from '../../../../hooks/useUsers'
import { formatCurrency, formatMileage } from '../../../../utils/formatters'
import { cn } from '../../../../utils/cn'

interface LeadEditTabsProps {
  lead: Lead | null
  formData: LeadEditFormData
  formattedValues: LeadEditFormattedValues
  checkPhoneWarning: string | null
  checkDocumentWarning?: string | null
  isCheckingPhone?: boolean
  isCheckingDocument?: boolean
  onFormDataChange: (updates: Partial<LeadEditFormData>) => void
  onFormattedValuesChange: (updates: Partial<LeadEditFormattedValues>) => void
  onInterestChange: (field: keyof LeadInterest, value: any) => void
  onMultiSelectChange: (field: keyof LeadInterest, value: string) => void
  onCreateProposal?: (vehicleId: string) => void
  leadId?: string
  onCancel?: () => void
  onSubmit?: () => Promise<{ success: boolean; message: string } | void>
  isSubmitting?: boolean
  validationErrors?: Record<string, string>
  onNextStep?: (currentStep: number, targetStep: number) => boolean
  duplicateLeadId?: string | null
  onDuplicateExit?: () => void
  onDuplicateRedirect?: () => void
  showDuplicatePopup?: boolean
}

export const LeadEditTabs: React.FC<LeadEditTabsProps> = ({
  formData,
  formattedValues,
  checkPhoneWarning,
  checkDocumentWarning = null,
  isCheckingPhone = false,
  isCheckingDocument = false,
  onFormDataChange,
  onFormattedValuesChange,
  onInterestChange,
  onMultiSelectChange,
  onCreateProposal,
  leadId,
  onCancel,
  onSubmit,
  isSubmitting = false,
  validationErrors = {},
  onNextStep,
  duplicateLeadId,
  onDuplicateExit,
  onDuplicateRedirect,
  showDuplicatePopup = false,
}) => {
  const t = (key: string) => i18n.t(key)
  const { options: vehicleOptions } = useVehicleOptions()
  const { options: leadOptions } = useLeadOptions()
  const { users } = useUsers()

  const [currentStep, setCurrentStep] = useState(0)

  const steps = React.useMemo(() => ([
    { id: 'basic', label: 'Informações', shortLabel: 'Info', icon: User },
    { id: 'preferences', label: 'Preferências', shortLabel: 'Prefs', icon: Sparkles },
    { id: 'seller', label: 'Atendimento', shortLabel: 'Atend.', icon: Users },
  ]), [])

  const handleMinPriceChange = (value: string) => onFormattedValuesChange({ minPriceFormatted: value })
  const handleMaxPriceChange = (value: string) => onFormattedValuesChange({ maxPriceFormatted: value })
  const handlePriceSuggestion = (value: number) => onFormattedValuesChange({ maxPriceFormatted: formatCurrency(value) })
  const handleMinMileageChange = (value: string) => onFormattedValuesChange({ minMileageFormatted: value })
  const handleMaxMileageChange = (value: string) => onFormattedValuesChange({ maxMileageFormatted: value })
  const handleMileageSuggestion = (value: number) => onFormattedValuesChange({ maxMileageFormatted: formatMileage(value) })

  const isLastStep = currentStep === steps.length - 1

  // Steps with validation errors (to show indicator)
  const stepsWithErrors: number[] = []
  if (validationErrors.name || validationErrors.phones || validationErrors.emails) {
    stepsWithErrors.push(0)
  }

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6">

      {/* Stepper indicator */}
      <div className="relative mx-auto flex w-full max-w-lg items-center justify-between px-1">
        {/* Connecting line */}
        <div className="absolute left-0 right-0 top-5 h-px bg-border -z-0" />

        {steps.map((step, index) => {
          const Icon = step.icon
          const isDone = index < currentStep
          const isActive = index === currentStep
          const isFuture = index > currentStep
          const hasError = stepsWithErrors.includes(index)

          return (
            <button
              key={step.id}
              type="button"
              onClick={() => setCurrentStep(index)}
              className="relative flex flex-col items-center gap-1.5 group z-10"
            >
              {/* Circle */}
              <div className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-200 font-medium text-sm bg-card',
                isDone && !hasError && 'bg-accent border-accent text-white',
                isActive && !hasError && 'border-accent text-accent shadow-sm ring-4 ring-accent/10',
                isFuture && !hasError && 'border-border text-muted-foreground group-hover:border-accent/40 group-hover:text-accent',
                hasError && 'border-destructive text-destructive ring-4 ring-destructive/10',
              )}>
                {isDone && !hasError
                  ? <Check className="h-4 w-4" />
                  : <Icon className="h-4 w-4" />
                }
              </div>

              {/* Label */}
              <span className={cn(
                'text-[0.625rem] font-semibold uppercase tracking-wide whitespace-nowrap hidden sm:block',
                isActive ? 'text-accent' : hasError ? 'text-destructive' : 'text-muted-foreground'
              )}>
                {step.label}
              </span>
              <span className={cn(
                'text-[0.625rem] font-semibold uppercase tracking-wide whitespace-nowrap sm:hidden',
                isActive ? 'text-accent' : hasError ? 'text-destructive' : 'text-muted-foreground'
              )}>
                {step.shortLabel}
              </span>
            </button>
          )
        })}
      </div>

      {/* Validation summary at top of step 0 */}
      {currentStep === 0 && Object.keys(validationErrors).length > 0 && (
        <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/20">
          <p className="text-sm font-medium text-destructive mb-1">Campos obrigatórios:</p>
          <ul className="space-y-0.5">
            {Object.values(validationErrors).map((msg, i) => (
              <li key={i} className="text-xs text-destructive">• {msg}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Step content */}
      <div className="min-h-[240px] w-full">
        {currentStep === 0 && (
          <BasicInfoTab
            name={formData.name}
            phones={formData.phones}
            emails={formData.emails}
            document={formData.document}
            cnh={formData.cnh}
            message={formData.message}
            vehicleInterests={formData.vehicleInterests}
            onNameChange={(value: string) => onFormDataChange({ name: value })}
            onPhonesChange={(phones) => onFormDataChange({ phones })}
            onEmailsChange={(emails) => onFormDataChange({ emails })}
            onDocumentChange={(value: string, type: 'cpf' | 'cnpj') => onFormDataChange({ document: value })}
            onCnhChange={(value: string) => onFormDataChange({ cnh: value })}
            onMessageChange={(value: string) => onFormDataChange({ message: value })}
            onVehicleInterestsChange={(interests) => onFormDataChange({ vehicleInterests: interests })}
            checkPhoneWarning={checkPhoneWarning}
            checkDocumentWarning={checkDocumentWarning}
            isCheckingPhone={isCheckingPhone}
            isCheckingDocument={isCheckingDocument}
            nameError={validationErrors.name}
            phonesError={validationErrors.phones}
          />
        )}

        {currentStep === 1 && (
          <PreferencesTab
            interest={formData.interest}
            minPriceFormatted={formattedValues.minPriceFormatted}
            maxPriceFormatted={formattedValues.maxPriceFormatted}
            minMileageFormatted={formattedValues.minMileageFormatted}
            maxMileageFormatted={formattedValues.maxMileageFormatted}
            vehicleOptions={vehicleOptions}
            onInterestChange={onInterestChange}
            onMinPriceChange={handleMinPriceChange}
            onMaxPriceChange={handleMaxPriceChange}
            onMinMileageChange={handleMinMileageChange}
            onMaxMileageChange={handleMaxMileageChange}
            onPriceSuggestion={handlePriceSuggestion}
            onMileageSuggestion={handleMileageSuggestion}
            onMultiSelectChange={onMultiSelectChange}
          />
        )}

        {currentStep === 2 && (
          <SellerTab
            sellerId={formData.sellerId}
            source={formData.source}
            notes={formData.notes}
            contactCount={formData.contactCount}
            engagement={formData.engagement}
            purchaseIntent={formData.purchaseIntent}
            usageType={formData.usageType}
            vehicleMoment={formData.vehicleMoment}
            hasTradeIn={formData.hasTradeIn}
            tradeInVehicleDescription={formData.tradeInVehicleDescription}
            tradeInVehicles={formData.tradeInVehicles}
            tradeInEvaluationEligible={formData.tradeInEvaluationEligible}
            tradeInEvaluationScore={formData.tradeInEvaluationScore}
            tradeInEstimatedValue={formData.tradeInEstimatedValue}
            tradeInEvaluationNotes={formData.tradeInEvaluationNotes}
            wantsFinancing={formData.wantsFinancing}
            financingWithTradeIn={formData.financingWithTradeIn}
            downPaymentAmount={formData.downPaymentAmount}
            downPaymentEntries={formData.downPaymentEntries}
            desiredInstallmentAmount={formData.desiredInstallmentAmount}
            approvedCreditAmount={formData.approvedCreditAmount}
            creditScore={formData.creditScore}
            financingNotes={formData.financingNotes}
            sellers={users.map(user => ({ value: user.id, label: user.name }))}
            sourceOptions={leadOptions.sources.map((s: { value: string; label: string }) => ({ value: s.value, label: s.label }))}
            onSellerChange={(value) => onFormDataChange({ sellerId: value })}
            onSourceChange={(value) => onFormDataChange({ source: value })}
            onNotesChange={(value) => onFormDataChange({ notes: value })}
            onFieldChange={(field, value) => onFormDataChange({ [field]: value } as Partial<LeadEditFormData>)}
          />
        )}

      </div>

      {/* Step navigation + actions */}
      <div className="flex flex-col gap-3 border-t border-border pt-5 sm:flex-row sm:items-center sm:justify-between">
        {/* Cancel / Previous */}
        <div className="flex flex-wrap items-center gap-2">
          {onCancel && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onCancel}
              disabled={isSubmitting}
              className="text-muted-foreground"
            >
              Cancelar
            </Button>
          )}
          {currentStep > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCurrentStep((s) => s - 1)}
              disabled={isSubmitting}
            >
              ← Anterior
            </Button>
          )}
        </div>

        {/* Next / Save */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="order-last text-xs text-muted-foreground sm:order-none sm:mr-1">
            {currentStep + 1} / {steps.length}
          </span>

          {isLastStep ? (
            onSubmit && (
              <Button
                type="button"
                size="sm"
                onClick={onSubmit}
                disabled={isSubmitting}
                className="w-fit"
              >
                {isSubmitting ? 'Salvando...' : 'Salvar Lead'}
              </Button>
            )
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={() => {
                if (currentStep === 0 && onNextStep) {
                  const canProceed = onNextStep(currentStep, currentStep + 1)
                  if (canProceed) {
                    setCurrentStep((s) => s + 1)
                  }
                } else {
                  setCurrentStep((s) => s + 1)
                }
              }}
              disabled={isSubmitting}
              className="w-fit"
            >
              Próximo →
            </Button>
          )}
        </div>
      </div>

      {/* Duplicate Lead Popup */}
      {showDuplicatePopup && duplicateLeadId && (
        <Dialog 
          isOpen={showDuplicatePopup} 
          onClose={onDuplicateExit || (() => {})}
          title="Cliente já cadastrado"
          description="Já existe um cadastro para este cliente."
          size="sm"
        >
          <div className="flex flex-col gap-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Deseja abrir o cliente existente ou sair deste formulário?
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={onDuplicateExit}>
                Sair
              </Button>
              <Button onClick={onDuplicateRedirect}>
                Abrir Cliente
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  )
}
